import { useEffect, useState } from 'react';
import { get } from '../../utils/fetch';
import camelCaseKeys from 'camelcase-keys';

export type LinkedTask = {
  id: number;
  localTaskId: number;
  title: string;
  description: string;
  priority: number;
  canView: boolean;
  open: boolean;
};

type RelatedToTaskIdsKey =
  | 'merged_to'
  | 'merged_from'
  | 'mergedTo'
  | 'mergedFrom';

type Args = {
  bugherdUrl: string;
  projectId: number;
  activeTask?: {
    id?: number;
    relatedToTaskIds?: {
      [key in RelatedToTaskIdsKey]?: number[];
    };
  } | null;
  isAdminView?: boolean;
};

export const useLinkedTasks = ({ bugherdUrl, projectId, activeTask }: Args) => {
  const [linkedTasks, setLinkedTasks] = useState<LinkedTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getLinkedTasks = async () => {
    if (!activeTask?.id) {
      setLinkedTasks([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await get(
        `${bugherdUrl}/projects/${projectId}/tasks/${activeTask.id}/linked_tasks`
      );
      const { linkedTasks }: { linkedTasks?: LinkedTask[] } = camelCaseKeys(
        response,
        {
          deep: true
        }
      );
      setLinkedTasks(linkedTasks || []);
    } catch (error) {
      console.error('Failed to fetch linked tasks:', error);
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  // Always fetch when task changes so linked tasks are up to date after merge (task may have stale relatedToTaskIds)
  useEffect(() => {
    getLinkedTasks();
  }, [bugherdUrl, projectId, activeTask?.id]);

  return { linkedTasks, loading, error };
};
